/**
 * @fileoverview TransactionTypeReport - Main component for transaction type filtering and reporting.
 *
 * @description This component provides:
 * - Transaction type selector (Expense, Income, Transfer, Adjustment)
 * - Date range picker (start and end dates, inclusive)
 * - Summary statistics display
 * - Filtered transaction list
 */
import { useState, useEffect } from 'react';
import { transactionAPI } from '../../services/api';
import { formatCurrency } from '../../utils/moneyUtils';
import ReportTransactionList from './ReportTransactionList';
import styles from './TransactionTypeReport.module.css';

/**
 * Get the first and last day of the current month in YYYY-MM-DD format
 */
const getCurrentMonthDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    firstDay: formatDate(firstDay),
    lastDay: formatDate(lastDay)
  };
};

/**
 * Transaction Type Report Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.budgetInfo - Budget metadata including start month
 */
function TransactionTypeReport({ budgetInfo }) {
  const { firstDay, lastDay } = getCurrentMonthDates();
  const [transactionType, setTransactionType] = useState('Expense');
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset report state when budget changes
  useEffect(() => {
    setReportData(null);
    setHasSearched(false);
    setError(null);
    setTransactionType('Expense');
    setStartDate(firstDay);
    setEndDate(lastDay);
  }, [budgetInfo?.budgetKey, firstDay, lastDay]);

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate start date is not after end date
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date. Please adjust your date range.');
      return;
    }

    // Validate dates are not before budget start date
    if (budgetInfo?.startMonth) {
      const budgetStartDate = budgetInfo.startMonth.split('T')[0];
      if (new Date(startDate) < new Date(budgetStartDate)) {
        setError(`Start date cannot be before the budget start date (${budgetStartDate}).`);
        return;
      }
      if (new Date(endDate) < new Date(budgetStartDate)) {
        setError(`End date cannot be before the budget start date (${budgetStartDate}).`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const data = await transactionAPI.getTransactionTypeReport(transactionType, startDate, endDate);
      setReportData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch report data');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setReportData(null);
    setHasSearched(false);
    setError(null);
  };

  return (
    <div className={styles.container}>
      {/* Search Filters Section */}
      <div className={styles.filtersSection}>
        <h2 className={styles.sectionTitle}>Search Filters</h2>

        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label className={styles.label}>Transaction Type:</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className={styles.select}
            >
              <option value="Expense">Expense</option>
              <option value="Income">Income</option>
              <option value="Transfer">Transfer</option>
              <option value="Adjustment">Adjustment</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.label}>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={budgetInfo?.startMonth ? budgetInfo.startMonth.split('T')[0] : undefined}
              className={styles.dateInput}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.label}>End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || (budgetInfo?.startMonth ? budgetInfo.startMonth.split('T')[0] : undefined)}
              className={styles.dateInput}
            />
          </div>
        </div>

        <div className={styles.buttonRow}>
          <button onClick={handleSearch} className={styles.searchButton} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasSearched && (
            <button onClick={handleClearSearch} className={styles.clearButton}>
              Clear Results
            </button>
          )}
        </div>

        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}
      </div>

      {/* Results Section */}
      {hasSearched && reportData && !loading && (
        <>
          {/* Summary Statistics */}
          <div className={styles.summarySection}>
            <h2 className={styles.sectionTitle}>Summary</h2>

            <div className={styles.summaryCards}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Total Transactions</div>
                <div className={styles.summaryValue}>{reportData.summary.transactionCount}</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Total Amount</div>
                <div className={styles.summaryValue}>
                  {formatCurrency(reportData.summary.totalAmount)}
                </div>
              </div>
            </div>

            <h3 className={styles.categoryBalancesTitle}>Category Balances</h3>
            <div className={styles.categoryBalancesTable}>
              <div className={styles.tableHeader}>
                <span className={styles.headerCell}>Category</span>
                <span className={styles.headerCell}>Total</span>
                <span className={styles.headerCell}>In (Credits)</span>
                <span className={styles.headerCell}>Out (Debits)</span>
              </div>
              {reportData.summary.categorySummaries.map((category) => (
                <div key={category.categoryKey} className={styles.tableRow}>
                  <span className={styles.cell}>{category.categoryName}</span>
                  <span className={styles.cell}>{formatCurrency(category.total)}</span>
                  <span className={`${styles.cell} ${styles.creditCell}`}>
                    {formatCurrency(Math.abs(category.credits))}
                  </span>
                  <span className={`${styles.cell} ${styles.debitCell}`}>
                    {formatCurrency(Math.abs(category.debits))}
                  </span>
                </div>
              ))}
              {reportData.summary.categorySummaries.length === 0 && (
                <div className={styles.emptyRow}>No categories found for this search</div>
              )}
            </div>
          </div>

          {/* Transaction List */}
          <div className={styles.transactionsSection}>
            <h2 className={styles.sectionTitle}>Transactions</h2>
            <ReportTransactionList transactions={reportData.transactions} />
          </div>
        </>
      )}

      {hasSearched && reportData && reportData.transactions.length === 0 && !loading && (
        <div className={styles.noResultsMessage}>
          No transactions found for {transactionType} between {startDate} and {endDate}
        </div>
      )}
    </div>
  );
}

export default TransactionTypeReport;
